import React from 'react';
import { 
    List, 
    Datagrid, 
    TextField, 
    NumberField, 
    Edit, 
    Create, 
    EditButton, 
    DeleteButton, 
    CloneButton, 
    SimpleForm, 
    TextInput, 
    NumberInput, 
    SelectInput, 
    ReferenceArrayInput, 
    SelectArrayInput,
    AutocompleteArrayInput,
    required,
    Toolbar,
    SaveButton,
    ListButton,
    TopToolbar,
    Pagination,
    ChipField,
    SingleFieldList,
    ReferenceManyField,
    FunctionField,
    Filter,
    SearchInput,
    useReferenceArrayInputContext,
    useLoading,
} from 'react-admin';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useForm, useFormState } from 'react-final-form';
import './autocomplete-chips.css';
import CircularProgress from '@material-ui/core/CircularProgress';

const SubscriptionModelPagination = props => <Pagination rowsPerPageOptions={[100, 50, 25, 10]} {...props} />;

const subscriptionPeriodChoices = [
    { id: 'daily', name: 'Daily' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'annually', name: 'Annually' },
];

const SubscriptionModelFilter = (props) => (
    <Filter {...props}>
        <SearchInput source="search" alwaysOn />
        <SelectInput 
            source="subscription_period_type" 
            choices={subscriptionPeriodChoices}
            label="Period Type"
        />
    </Filter>
);

// Autocomplete for Associated Sites with a loader icon visually
// aligned to the right edge of the autocomplete input, using the
// same global loading flag as the navbar plus the field loading state.
const AssociatedSitesAutocompleteInput = (props) => {
    const { loading: refLoading } = useReferenceArrayInputContext();
    const globalLoading = useLoading();
    const loading = !!(globalLoading || refLoading);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <AutocompleteArrayInput
                {...props}
                optionText="name"
                filterToQuery={searchText => ({ q: searchText })}
                options={{
                    suggestionsContainerProps: {
                        modifiers: {
                            flip: { enabled: false },
                        },
                    },
                }}
            />
            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        zIndex: 2,
                    }}
                >
                    <CircularProgress size={18} />
                </div>
            )}
        </div>
    );
};

export const SubscriptionModelList = props => (
    <List {...props} pagination={<SubscriptionModelPagination />} filters={<SubscriptionModelFilter />}>
        <Datagrid rowClick="edit">
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="subscription_period_type" label="Period Type" />
            <NumberField source="cycle_limit" label="Cycle Limit" />
            <FunctionField 
                label="Sites Count" 
                render={record => record.sites_count || 0} 
            />
            <ReferenceManyField 
                label="Sites" 
                reference="sites" 
                target="subscription_models" 
                allowEmpty 
                perPage={5}
            >
                <SingleFieldList>
                    <ChipField source="name" />
                </SingleFieldList>
            </ReferenceManyField>
            <EditButton />
            <DeleteButton mutationMode={"pessimistic"} />
            <CloneButton />
        </Datagrid>
    </List>
);

export const SubscriptionModelEdit = props => (
    <Edit title={<SubscriptionModelTitle />} actions={<SubscriptionModelEditActions />} {...props}>
        <SimpleForm toolbar={<SubscriptionModelEditFormToolbar />}> 
            <SanitizeSitesSelection />
            <TextInput source="id" disabled />
            <TextInput source="name" validate={required()} />
            <TextInput source="description" multiline rows={3} />
            <NumberInput 
                source="cycle_limit" 
                validate={required()} 
                min={1}
                label="Cycle Limit"
            />
            <SelectInput 
                source="subscription_period_type" 
                choices={subscriptionPeriodChoices}
                validate={required()}
                label="Period Type"
            />
            <ReferenceArrayInput 
                source="sites" 
                reference="sites"
                label="Associated Sites"
                perPage={500}
                format={value => Array.isArray(value) ? value : []}
                parse={value => Array.isArray(value) ? value : []}
            >
                <AssociatedSitesAutocompleteInput />
            </ReferenceArrayInput>
        </SimpleForm>
    </Edit>
);

export const SubscriptionModelCreate = props => (
    <Create {...props}>
        <SimpleForm redirect="list">
            <TextInput source="name" validate={required()} />
            <TextInput source="description" multiline rows={3} />
            <NumberInput 
                source="cycle_limit" 
                validate={required()} 
                min={1}
                label="Cycle Limit"
            />
            <SelectInput 
                source="subscription_period_type" 
                choices={subscriptionPeriodChoices}
                validate={required()}
                label="Period Type"
            />
            <ReferenceArrayInput 
                source="sites" 
                reference="sites"
                label="Associated Sites"
            >
                <AssociatedSitesAutocompleteInput />
            </ReferenceArrayInput>
        </SimpleForm>
    </Create>
);

const SubscriptionModelTitle = ({ record }) => {
    return <span>Subscription Model {record ? `"${record.name}"` : ''}</span>;
};

const SubscriptionModelEditActions = props => (
    <TopToolbar {...props} style={{ justifyContent: 'flex-start' }}>
        <ListButton label="Back" icon={<ArrowBackIcon />} />
    </TopToolbar>
);

const SubscriptionModelEditFormToolbar = props => (
    <Toolbar {...props}>
        <SaveButton alwaysEnable />
        <DeleteButton mutationMode={"pessimistic"} />
    </Toolbar>
);

// Remove invalid site ids to avoid 404s on getMany
const SanitizeSitesSelection = () => {
    const form = useForm();
    const { values } = useFormState();
    const currentSites = values && values.sites;
    React.useEffect(() => {
        const current = currentSites;
        if (!current) return;
        if (!Array.isArray(current)) {
            form.change('sites', []);
            return;
        }
        const cleaned = current.filter(id => Number.isInteger(id));
        if (cleaned.length !== current.length) {
            form.change('sites', cleaned);
        }
    }, [form, currentSites]);
    return null;
};
